/**
 * Gestão de Operadores do White Label (Fase 1)
 *
 * Lista todos os wl_users e permite criar novos vinculando a um auth.user
 * existente (por e-mail) e a um wl_role.
 *
 * Leiaute padronizado seguindo o mesmo padrão da página Agentes (tenant):
 *  - Header compacto com borda inferior
 *  - 4 KPI cards horizontais (Total / Ativos / Suspensos / Perfis)
 *  - Tabela premium com SmartAvatar, ícone por perfil, StatusDot e ações em dropdown
 *  - EmptyState quando não há registros
 *
 * RLS garante que somente operadores ativos podem ler/escrever (lógica intacta).
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import {
  Plus,
  Loader2,
  Users,
  UserCheck,
  Shield,
  ShieldCheck,
  User,
  Crown,
  MoreVertical,
  UserPlus,
  Ban,
  RefreshCw,
} from 'lucide-react';
import { toast } from '@/lib/toast';
import { useWlPermissions } from '@/contexts/WlPermissionsContext';
import { createLogger } from '@/lib/logger';
import { describeWlError } from '@/hooks/wl/useWlErrorToast';
import { cn } from '@/lib/utils';

const logger = createLogger('WlOperators');

interface WlUserRow {
  id: string;
  user_id: string;
  display_name: string;
  email: string;
  role_id: string;
  status: string;
  created_at: string;
  role?: { name: string; slug: string };
}
interface WlRoleRow { id: string; name: string; slug: string }

// ============================================================================
// COMPONENTES AUXILIARES (padrão visual unificado com TenantAgents)
// ============================================================================

/** Cores das variantes do MetricCard — alinhado a DealListKpiCards/TenantAgents */
const wlVariantStyles = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  warning: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
  info:    { cardBg: 'bg-blue-100 dark:bg-blue-900/30',  iconColor: 'text-blue-700 dark:text-blue-300' },
};

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  variant: keyof typeof wlVariantStyles;
  subtitle?: string;
}

/** Card de KPI compacto horizontal */
const MetricCard = ({ title, value, icon: Icon, variant, subtitle }: MetricCardProps) => {
  const styles = wlVariantStyles[variant];
  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
        </div>
        {subtitle && <p className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 truncate">{subtitle}</p>}
      </CardContent>
    </Card>
  );
};

interface EmptyStateProps {
  icon: React.ElementType;
  iconBgColor: string;
  iconColor: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}

/** Estado vazio com design moderno */
const EmptyState = ({ icon: Icon, iconBgColor, iconColor, title, description, action }: EmptyStateProps) => (
  <div className="border-dashed border-2 rounded-xl bg-gradient-to-br from-muted/30 to-muted/10 flex flex-col items-center justify-center py-16">
    <div className={`flex items-center justify-center w-16 h-16 rounded-full ${iconBgColor} mb-4`}>
      <Icon className={`h-8 w-8 ${iconColor}`} />
    </div>
    <h3 className="text-lg font-semibold mb-2">{title}</h3>
    <p className="text-muted-foreground text-center max-w-md mb-4">{description}</p>
    {action}
  </div>
);

/** Mapeia slug do perfil WL para ícone visual */
const wlRoleIcon = (slug?: string) => {
  if (slug === 'owner_wl') return Crown;
  if (slug === 'admin_wl') return ShieldCheck;
  if (slug === 'support_n1' || slug === 'support_n2') return Shield;
  return User;
};

/** Cor do ícone do perfil */
const wlRoleIconClass = (slug?: string) => {
  if (slug === 'owner_wl') return 'text-yellow-600';
  if (slug === 'admin_wl') return 'text-primary';
  if (slug === 'support_n1' || slug === 'support_n2') return 'text-purple-600';
  return 'text-muted-foreground';
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function WlOperators() {
  const { canCreate, canEdit, isObserver } = useWlPermissions();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<WlUserRow[]>([]);
  const [roles, setRoles] = useState<WlRoleRow[]>([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Formulário de criação
  const [emailLookup, setEmailLookup] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleId, setRoleId] = useState('');

  const writable = canCreate('wl.wl_users') && !isObserver;
  const editable = canEdit('wl.wl_users') && !isObserver;

  const fetchData = async () => {
    setLoading(true);
    try {
      // Server-side abstraction: RPCs ao invés de SELECT direto em tabelas
      const [operatorsRes, rolesRes] = await Promise.all([
        supabase.rpc('wl_list_operators'),
        supabase.rpc('wl_list_roles_with_menus'),
      ]);
      if (operatorsRes.error) throw operatorsRes.error;
      if (rolesRes.error) throw rolesRes.error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setRows((operatorsRes.data ?? []).map((r: any) => ({
        id: r.id,
        user_id: r.user_id,
        display_name: r.display_name,
        email: r.email,
        role_id: r.role_id,
        status: r.status,
        created_at: r.created_at,
        role: r.role_name ? { name: r.role_name, slug: r.role_slug } : undefined,
      })));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rolesPayload = (rolesRes.data as any)?.roles ?? [];
      setRoles(rolesPayload);
    } catch (err) {
      logger.error('Erro ao carregar operadores', { err });
      toast.error('Erro ao carregar operadores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!emailLookup || !displayName || !roleId) {
      toast.error('Preencha todos os campos');
      return;
    }
    setSubmitting(true);
    try {
      // RPC dedicada: faz lookup do e-mail + valida permissão + insere
      const { error } = await supabase.rpc('wl_create_operator', {
        p_email: emailLookup.trim().toLowerCase(),
        p_display_name: displayName.trim(),
        p_role_id: roleId,
      });
      if (error) throw error;
      toast.success('Operador criado');
      setOpen(false);
      setEmailLookup(''); setDisplayName(''); setRoleId('');
      fetchData();
    } catch (err) {
      logger.error('Erro ao criar operador', { err });
      toast.error(describeWlError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async (row: WlUserRow) => {
    if (!editable) return;
    const next = row.status === 'active' ? 'suspended' : 'active';
    // Server-side abstraction: RPC ao invés de UPDATE direto
    const { error } = await supabase.rpc('wl_set_operator_status', { p_id: row.id, p_status: next });
    if (error) { toast.error(describeWlError(error)); return; }
    toast.success(`Operador ${next === 'active' ? 'reativado' : 'suspenso'}`);
    fetchData();
  };

  // KPIs derivados (sem nova RPC)
  const totalOperators  = rows.length;
  const activeOperators = rows.filter(r => r.status === 'active').length;
  const suspendedOperators = rows.filter(r => r.status === 'suspended').length;
  const availableRoles = roles.filter(r => r.slug !== 'owner_wl').length;

  // Botão de criação (reaproveitado em header e empty state)
  const renderCreateDialog = (trigger: React.ReactNode) => (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Novo Operador WL</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>E-mail (já cadastrado no sistema)</Label>
            <Input value={emailLookup} onChange={e => setEmailLookup(e.target.value)} placeholder="usuario@empresa.com" />
          </div>
          <div className="space-y-2">
            <Label>Nome exibido</Label>
            <Input value={displayName} onChange={e => setDisplayName(e.target.value)} maxLength={120} />
          </div>
          <div className="space-y-2">
            <Label>Perfil</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger><SelectValue placeholder="Selecione um perfil" /></SelectTrigger>
              <SelectContent>
                {/* Owner WL é perfil de sistema imutável e não deve ser atribuível via UI */}
                {roles.filter(r => r.slug !== 'owner_wl').map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancelar</Button>
          <Button onClick={handleCreate} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Criar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header compacto — padrão TenantAgents */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Operadores WL</h1>
          <p className="text-xs text-muted-foreground">
            Equipe interna com acesso ao painel /wl
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {writable && renderCreateDialog(
            <Button size="xs">
              <UserPlus className="mr-1.5" />
              Novo Operador
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
        <MetricCard
          title="Total Operadores"
          value={totalOperators}
          icon={Users}
          variant="primary"
        />
        <MetricCard
          title="Ativos"
          value={activeOperators}
          icon={UserCheck}
          variant="success"
        />
        <MetricCard
          title="Suspensos"
          value={suspendedOperators}
          icon={Shield}
          variant="warning"
        />
        <MetricCard
          title="Perfis Disponíveis"
          value={availableRoles}
          icon={Crown}
          variant="info"
        />
      </div>

      {/* Conteúdo principal */}
      <div className="px-4 pb-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Users}
            iconBgColor="bg-primary/10"
            iconColor="text-primary/70"
            title="Nenhum operador cadastrado"
            description="Cadastre o primeiro operador para liberar o acesso ao painel WL"
            action={
              writable
                ? renderCreateDialog(
                    <Button>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Cadastrar Primeiro Operador
                    </Button>
                  )
                : undefined
            }
          />
        ) : (
          <div className="rounded-xl border bg-gradient-to-br from-background to-muted/10 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/40">
                  <TableHead className="font-semibold">Operador</TableHead>
                  <TableHead className="font-semibold">E-mail</TableHead>
                  <TableHead className="font-semibold">Perfil</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Criado em</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => {
                  const RoleIcon = wlRoleIcon(r.role?.slug);
                  const isOwner = r.role?.slug === 'owner_wl';
                  const isAdminWl = r.role?.slug === 'admin_wl';
                  // Admin WL ativo não pode ser suspenso (defesa em profundidade no banco).
                  // Mantém "Reativar" disponível caso exista admin WL legado suspenso.
                  const canToggle = editable && !(isAdminWl && r.status === 'active');

                  return (
                    <TableRow key={r.id} className="group hover:bg-muted/20 transition-colors">
                      {/* Operador (Avatar + Nome + badge Owner) */}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <SmartAvatar
                            name={r.display_name}
                            email={r.email}
                            size="md"
                            className="border-2 border-muted"
                          />
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{r.display_name || 'Sem nome'}</span>
                            {isOwner && (
                              <Badge variant="outline" className="text-yellow-600 border-yellow-500/30 bg-yellow-500/10 text-[10px] px-1.5 py-0">
                                <Crown className="h-2.5 w-2.5 mr-0.5" />
                                Owner WL
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* E-mail */}
                      <TableCell className="text-muted-foreground text-sm">
                        {r.email || '—'}
                      </TableCell>

                      {/* Perfil (ícone + label) */}
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm">
                          <RoleIcon className={cn('h-3.5 w-3.5', wlRoleIconClass(r.role?.slug))} />
                          <span>{r.role?.name ?? '—'}</span>
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                        >
                          <StatusDot tone={r.status === 'active' ? 'emerald' : 'muted'} />
                          {r.status === 'active' ? 'Ativo' : 'Suspenso'}
                        </Badge>
                      </TableCell>

                      {/* Criado em */}
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(r.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>

                      {/* Ações */}
                      <TableCell>
                        {canToggle && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => toggleStatus(r)}>
                                {r.status === 'active' ? (
                                  <>
                                    <Ban className="h-4 w-4 mr-2" />
                                    Suspender
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Reativar
                                  </>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
